import { createApi } from "@reduxjs/toolkit/query/react";
import axios from "axios";
import { logout, setCredentials } from "../slices/authSlice";

// สร้าง axios instance
const axiosInstance = axios.create({
  baseURL: `${import.meta.env.VITE_API_URL}/api`,
  timeout: 10000,
});

const axiosBaseQuery =
  ({ baseUrl } = { baseUrl: "" }) =>
  async (args, api, extraOptions) => {
    const { url, method, data, params, headers } = args;

    try {
      // ดึง token จาก Redux store
      const state = api.getState();
      const token = state.auth?.token;

      // ตั้งค่า headers
      const config = {
        url: baseUrl + url,
        method: method || "GET",
        data,
        params,
        headers: {
          ...headers,
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      };

      const result = await axiosInstance(config);
      return { data: result.data };
    } catch (axiosError) {
      const error = axiosError;

      // ถ้าเป็น 403 และมี refresh token
      if (error.response?.status === 403) {
        const state = api.getState();
        const refreshToken = state.auth?.refreshToken;

        if (refreshToken) {
          try {
            // พยายาม refresh token
            const refreshResult = await axios.post(
              `${import.meta.env.VITE_API_URL}/api/auth/refresh`,
              { refreshToken }
            );

            const { accessToken, refreshToken: newRefreshToken } =
              refreshResult.data;

            // อัพเดท Redux store
            api.dispatch(
              setCredentials({
                accessToken,
                refreshToken: newRefreshToken || refreshToken,
              })
            );

            // Retry original request
            const retryConfig = {
              ...args,
              headers: {
                ...args.headers,
                Authorization: `Bearer ${accessToken}`,
              },
            };

            const retryResult = await axiosInstance(retryConfig);
            return { data: retryResult.data };
          } catch (refreshError) {
            // Refresh ไม่สำเร็จ - logout
            api.dispatch(logout());
            return {
              error: {
                status: 401,
                data: "Session expired",
                message: "ກະລຸນາເຂົ້າສູ່ລະບົບໃໝ່ອີກຄັ້ງ",
              },
            };
          }
        } else {
          // ไม่มี refresh token - logout
          api.dispatch(logout());
        }
      }

      // จัดการ error อื่นๆ
      const errorResponse = {
        status: error.response?.status || "FETCH_ERROR",
        data: error.response?.data || error.message,
        message: getErrorMessage(error),
      };

      // ส่ง error notification
      handleErrorNotification(error, api);

      return { error: errorResponse };
    }
  };

// ฟังก์ชันสร้าง error message
const getErrorMessage = (error) => {
  const status = error.response?.status;
  const message = error.response?.data?.message || error.message;

  switch (status) {
    case 400:
      return `ຂໍ້ມູນບໍ່ຖືກຕ້ອງ: ${message}`;
    case 403:
      return "ທ່ານບໍ່ມີສິດເຂົ້າເຖິງຂໍ້ມູນນິ້";
    case 404:
      return "ບໍ່ພົບຂໍ້ມູນທີ່ຕ້ອງການ";
    case 422:
      return `ຂໍ້ມູນບໍ່ຖືກຕ້ອງ: ${message}`;
    case 500:
      return message || "ເກີດຂໍ້ຜິດພາດໃນລະບົບ";
    default:
      return message || "ເກີດຂໍ້ຜິດພາດ,ກະລຸນາລອງໃໝ່ອີກຄັ້ງ";
  }
};

// ฟังก์ชันจัดการ notification
const handleErrorNotification = (error, api) => {
  const status = error.response?.status;

  // สามารถ dispatch action ไปยัง notification slice ได้
  // api.dispatch(showNotification({
  //   type: 'error',
  //   message: getErrorMessage(error)
  // }));

  // หรือใช้ toast library
  if (typeof window !== "undefined" && window.toast) {
    window.toast.error(getErrorMessage(error));
  }
};

export const apiService = createApi({
  baseQuery: axiosBaseQuery(),
  endpoints: () => ({}),
  reducerPath: "apiService",
  tagTypes: ["User", "Ticket", "Admin", "Checkpoint", "Dashboard","ReportCarInCarOut"],
  keepUnusedDataFor: 60, // เก็บ cache 1 นาที
  refetchOnMountOrArgChange: 30, // refetch ถ้าข้อมูลเก่ากว่า 30 วินาที
  refetchOnFocus: true, // refetch เมื่อกลับมา focus หน้าต่าง
  refetchOnReconnect: true, // refetch เมื่อเชื่อมต่อเน็ตใหม่
});

export default apiService;
